import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useFocusOnKey } from '@/utils/keyboard';
import { ExpandedView } from './components';
import { CollapsibleQueryPanel } from './CollapsibleQueryPanel';
import { useQueryPanel, useQueryPanelShake } from './hooks';
import {
    queryPanelPresenceTransition,
    queryPanelPresenceProps,
    QUERY_PANEL_EASE,
    QUERY_PANEL_MOTION_DURATION_S,
} from './queryPanelPresenceMotion';

interface QueryPanelProps {
    compact?: boolean;
    layoutId?: string;
    prefix?: string;
}

export interface QueryPanelRef {
    focusAndExpand: () => void;
}

export const QueryPanel = forwardRef<QueryPanelRef, QueryPanelProps>(
    ({ compact = false, layoutId, prefix }, ref) => {
        const state = useQueryPanel({ compact });
        const prefersReducedMotion = useReducedMotion();
        const shakeRef = useQueryPanelShake(state.shakeNonce);

        const presenceMotion = useMemo(
            () => ({
                ...queryPanelPresenceProps(prefersReducedMotion),
                transition: queryPanelPresenceTransition(prefersReducedMotion),
            }),
            [prefersReducedMotion],
        );

        useFocusOnKey('s', state.inputRef, state.focusAndExpand);

        useImperativeHandle(ref, () => ({
            focusAndExpand: state.focusAndExpand,
        }));

        if (compact) {
            return (
                <CollapsibleQueryPanel
                    state={state}
                    prefix={prefix}
                    layoutId={layoutId}
                />
            );
        }

        const shellTransition = 'transition-all duration-200 ease-out-expo';
        const baseClasses = `relative max-w-content ${shellTransition}`;
        const sizeClasses = 'w-full h-auto rounded-[10px]';

        const layoutTransition = {
            type: 'tween' as const,
            duration: QUERY_PANEL_MOTION_DURATION_S,
            ease: QUERY_PANEL_EASE,
        };

        const Component = layoutId ? motion.div : 'div';
        const layoutProps = layoutId
            ? {
                  layoutId,
                  layout: true,
                  transition: layoutTransition,
              }
            : {};

        return (
            <Component
                role="search"
                className={`${baseClasses} ${sizeClasses}`}
                {...layoutProps}
            >
                <div className="relative">
                    <AnimatePresence initial={false}>
                        <motion.div
                            key="query-panel-expanded"
                            className="relative origin-center"
                            {...presenceMotion}
                        >
                            <motion.div
                                ref={shakeRef}
                                className="relative flex rounded-[10px] border border-grey-400 bg-grey-600 shadow-[4px_4px_48px_rgb(7_8_10)]"
                            >
                                <ExpandedView state={state} prefix={prefix} />
                            </motion.div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </Component>
        );
    },
);
